import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import useUnauthorizedHandler from "../../../../../../common/snippet/useUnauthorizedHandler";
import { debounce } from "../../../../../../common/snippet/Debouncing";
import { GetWorkersForClientInductionReport } from "../../../../../../actions/Admin-Client/reports/GetWorkersForClientInductionReport";

const toNumericId = (value, key) => {
  if (value == null) {
    return undefined;
  }

  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  if (typeof value === "object") {
    const candidate = value?.[key];
    if (candidate == null) {
      return undefined;
    }
    return toNumericId(candidate, key);
  }

  return undefined;
};

const toWorkerIdString = (value) => {
  if (value == null) {
    return undefined;
  }

  if (typeof value === "object") {
    if (value?.worker_id != null) {
      return toWorkerIdString(value.worker_id);
    }
    if (value?.workerId != null) {
      return toWorkerIdString(value.workerId);
    }
    if (value?.id != null) {
      return toWorkerIdString(value.id);
    }
    if (value?.value != null) {
      return toWorkerIdString(value.value);
    }

    return undefined;
  }

  const result = String(value).trim();
  return result.length ? result : undefined;
};

const normaliseIdArray = (source = [], key) => {
  return source
    .map((item) => toNumericId(item, key))
    .filter((item) => typeof item === "number");
};

const WorkerMultiSelectDropdown = ({
  title,
  updateFilter,
  selectedWorkerIds = [],
  clientOrgIds = [],
  providerOrgIds = [],
}) => {
  const dispatch = useDispatch();
  const handleUnauthorizedAccess = useUnauthorizedHandler();

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [items, setItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [hasError, setHasError] = useState(false);

  const dropdownRef = useRef(null);
  const inflightRequestsRef = useRef(new Map());
  const resultsCacheRef = useRef({});

  const normalisedClientIds = useMemo(
    () => normaliseIdArray(clientOrgIds, "organisation_id"),
    [clientOrgIds]
  );

  const normalisedProviderIds = useMemo(
    () => normaliseIdArray(providerOrgIds, "provider_org_id"),
    [providerOrgIds]
  );

  const hasProviderContext = normalisedProviderIds.length > 0;

  useEffect(() => {
    const normalised = Array.isArray(selectedWorkerIds)
      ? selectedWorkerIds.map((id) => toWorkerIdString(id)).filter(Boolean)
      : [];

    setSelectedIds(normalised);
  }, [JSON.stringify(selectedWorkerIds ?? [])]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!hasProviderContext) {
      if (selectedIds.length) {
        setSelectedIds([]);
        updateFilter("worker_ids", []);
      }

      setItems([]);
      setIsFetching(false);
      setHasError(false);
    }
  }, [hasProviderContext, selectedIds.length, updateFilter]);

  const fetchWorkers = useCallback(
    debounce(async (searchValue) => {
      const trimmedSearch = typeof searchValue === "string" ? searchValue.trim() : "";

      if (!hasProviderContext) {
        setItems([]);
        setIsFetching(false);
        setHasError(false);
        return;
      }

      const payload = {
        search: trimmedSearch,
      };

      if (normalisedClientIds.length) {
        payload.client_org_ids = JSON.stringify(normalisedClientIds);
      }

      if (normalisedProviderIds.length) {
        payload.provider_org_ids = JSON.stringify(normalisedProviderIds);
      }

      const requestKey = JSON.stringify({
        search: payload.search || "",
        client_org_ids: payload.client_org_ids || "",
        provider_org_ids: payload.provider_org_ids || "",
      });

      if (resultsCacheRef.current[requestKey]) {
        setItems(resultsCacheRef.current[requestKey]);
        setIsFetching(false);
        setHasError(false);
        return;
      }

      if (inflightRequestsRef.current.has(requestKey)) {
        setIsFetching(true);
        return;
      }

      setIsFetching(true);
      setHasError(false);
      inflightRequestsRef.current.set(requestKey, true);

      try {
        const response = await dispatch(GetWorkersForClientInductionReport(payload));

        if (response?.status === 401) {
          handleUnauthorizedAccess();
          setIsFetching(false);
          return;
        }

        if (!response || response.success === false) {
          setHasError(true);
          setItems([]);
          setIsFetching(false);
          return;
        }

        const rawWorkers = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.rows)
          ? response.data.rows
          : Array.isArray(response?.data?.workers)
          ? response.data.workers
          : [];

        const formatted = rawWorkers
          .map((worker) => {
            const workerId = toWorkerIdString(worker);
            if (!workerId) {
              return null;
            }

            const nameFallback = [worker?.first_name, worker?.last_name]
              .filter(Boolean)
              .join(" ");

            return {
              worker_id: workerId,
              name: worker?.worker_name || nameFallback || "Unknown worker",
            };
          })
          .filter(Boolean);

        resultsCacheRef.current[requestKey] = formatted;
        setItems(formatted);
        setIsFetching(false);
      } catch (error) {
        setHasError(true);
        setItems([]);
        setIsFetching(false);
      } finally {
        inflightRequestsRef.current.delete(requestKey);
      }
    }, 300),
    [dispatch, normalisedClientIds, normalisedProviderIds, hasProviderContext, handleUnauthorizedAccess]
  );

  useEffect(() => {
    if (!isOpen || !hasProviderContext) {
      if (!hasProviderContext) {
        setItems([]);
        setIsFetching(false);
        setHasError(false);
      }
      return;
    }

    fetchWorkers(searchTerm);
  }, [
    searchTerm,
    hasProviderContext,
    isOpen,
    fetchWorkers,
  ]);

  const toggleDropdown = () => {
    if (!hasProviderContext) {
      return;
    }

    setIsOpen((prev) => !prev);
  };

  const handleSelect = (worker) => {
    const id = toWorkerIdString(worker?.worker_id);
    if (!id) {
      return;
    }

    let updated;
    if (selectedIds.includes(id)) {
      updated = selectedIds.filter((value) => value !== id);
    } else {
      updated = [...selectedIds, id];
    }

    setSelectedIds(updated);
    const numericIds = updated
      .map((value) => {
        const parsed = Number(value);
        return Number.isNaN(parsed) ? null : parsed;
      })
      .filter((value) => value != null);

    updateFilter("worker_ids", numericIds);
  };

  const clearSelection = () => {
    setSelectedIds([]);
    updateFilter("worker_ids", []);
  };

  return (
    <div className="relative inline-block text-left w-34" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        type="button"
        disabled={!hasProviderContext}
        className={`flex items-center justify-between w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
          hasProviderContext
            ? "hover:bg-gray-50"
            : "opacity-60 cursor-not-allowed bg-gray-50"
        }`}
      >
        <span>{title}</span>
        {selectedIds.length > 0 && (
          <span className="inline-flex ml-1 items-center justify-center w-6 h-6 text-sm font-semibold text-blue-700 bg-blue-100 rounded-full">
            {selectedIds.length}
          </span>
        )}
        <svg
          className="w-5 h-5 ml-2 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-10">
          <div className="flex items-center justify-between p-2 border-b border-gray-200">
            <span className="text-sm text-gray-500">{selectedIds.length} selected</span>
            <button
              type="button"
              className="text-sm text-blue-600 hover:underline focus:outline-none"
              onClick={clearSelection}
            >
              Clear
            </button>
          </div>

          <div className="p-2">
            <input
              type="text"
              placeholder="Search workers"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>

          <ul className="max-h-48 overflow-y-auto">
            {isFetching && (
              <li className="px-3 py-2 text-sm text-gray-500">Loading workers…</li>
            )}
            {!isFetching && hasError && (
              <li className="px-3 py-2 text-sm text-error-600">Failed to load workers.</li>
            )}
            {!isFetching && !hasError && items.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-500">No workers found.</li>
            )}
            {!isFetching && !hasError &&
              items.map((item) => (
                <li key={item.worker_id} className="flex items-center px-3 py-2 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    className="w-4 h-4 mr-2 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    checked={selectedIds.includes(item.worker_id)}
                    onChange={() => handleSelect(item)}
                  />
                  <span className="text-sm text-gray-700">{item.name}</span>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default WorkerMultiSelectDropdown;
